import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';

import { Filter } from '../Filter';
import { useNavigationBarData } from '../NavigationBar/NavigationBarProvider';
import { useHeatMapFilterOptions } from '../../map-data/use-heat-map-filter-options';
import type { FilterConfig } from '../Filter/filter-types';
import { muiTheme, theme } from '../../styles/theme';
import { hexToRgb } from '../../utils/colour-utils';
import { activeStatusConfig } from '../../map-data/heatmap-legend-config';
import {
  heatmapFilterRegistry,
  buildYesNoOptions,
} from '../../map-data/heatmap-filter-registry';

export function HeatMapFilters() {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(
    `(min-width: ${theme.breakPoints.desktop}px)`,
  );

  const { selectedNavigationItem } = useNavigationBarData();
  const { resetFilterOptions, updateFilterOptions, filterConfig, rangeType } =
    useHeatMapFilterOptions();

  const reg = heatmapFilterRegistry;

  const statusFilterRow = {
    icon: reg.status.icon,
    title: t(reg.status.titleKey),
    key: reg.status.key,
    width: reg.status.width,
    clearValue: reg.status.clearValue,
    viewModes: reg.status.viewModes,
    keyOptions: [
      {
        label: t('heatMapExperience.filters.options.seeAll'),
        value: 'AllStatuses',
        color: '255, 255, 255',
        textColor: muiTheme.palette.text.primary,
      },
      ...activeStatusConfig.map(status => ({
        value: status.key,
        label: t(`countryStatus.${status.translationKey}`),
        color: hexToRgb(status.color),
      })),
    ],
    onUpdate: updateFilterOptions,
    filterType: 'multiChoiceToggle' as const,
  };

  const continentFilter = {
    icon: reg.continent.icon,
    title: t(reg.continent.titleKey),
    key: ['continent'],
    placeholder: t('poiExperience.filters.placeholder.region'),
    width: reg.continent.width,
    keyOptions: [
      { label: t('poiExperience.filters.options.all'), value: '' },
      ...(filterConfig?.continents ?? []).map(c => ({ label: c, value: c })),
    ],
    onUpdate: (props: Record<string, string | undefined>) => {
      updateFilterOptions({ continent: Object.values(props)[0] || undefined });
    },
    filterType: 'select' as const,
  };

  const buildYesNoFilter = (key: keyof typeof heatmapFilterRegistry) => {
    const def = reg[key];
    return {
      icon: def.icon,
      title: t(def.titleKey),
      key: def.key,
      viewModes: 'viewModes' in def ? def.viewModes : undefined,
      keyOptions: buildYesNoOptions(t),
      onUpdate: updateFilterOptions,
      filterType: 'multiChoice' as const,
    };
  };

  const heatmapFilterConfig: FilterConfig = [
    [
      ...(isDesktop ? [] : [statusFilterRow]),
      continentFilter,
      buildYesNoFilter('nccpAvailable'),
      buildYesNoFilter('lungCancerInNccp'),
      buildYesNoFilter('earlyDetectionInNccp'),
      buildYesNoFilter('fundingForEarlyDetection'),
    ],
  ];

  if (selectedNavigationItem !== 'filter') {
    return null;
  }

  return (
    <Filter
      filterConfig={heatmapFilterConfig}
      resetFilterOptions={resetFilterOptions}
      viewMode={rangeType}
    />
  );
}
